import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firestore, functions } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { isSuperAdmin } from "../shared/superAdmin";
import MobileTableCards from "../shared/MobileTableCards";
import "./AdminPanel.css";

// Cross-school developer console. Access is gated on the super-admin UID — the SAME
// account the firestore rules grant global read/write — because the global reads below
// only succeed for that account anyway. (The old admin/admin password was cosmetic.)

const DAY = 24 * 60 * 60 * 1000;

// Accepts a Firestore Timestamp, an ISO string (sign-out `time_out`), or null → Date|null.
const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

// Compact "how long ago" for the last-active column.
const timeAgo = (date) => {
  if (!date) return "never";
  const diff = Date.now() - date.getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? "just now" : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
};

const AdminPanel = () => {
  const { uid, user, loading: userLoading } = useUser();
  const allowed = isSuperAdmin({ uid, email: user?.email });

  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [soloTeachers, setSoloTeachers] = useState(0);
  const [fetching, setFetching] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [copiedCount, setCopiedCount] = useState(null);
  const [supportMsgs, setSupportMsgs] = useState([]);
  const [ghosts, setGhosts] = useState(null); // null = not scanned yet
  const [ghostScanning, setGhostScanning] = useState(false);
  const [ghostError, setGhostError] = useState("");

  // Ghost accounts = Auth users with no Firestore profile (school-less). Scanned
  // on demand (lists all Auth users server-side, so not on every panel load).
  const scanGhosts = async () => {
    setGhostScanning(true);
    setGhostError("");
    try {
      const fn = httpsCallable(functions, "listGhostAccounts");
      const { data } = await fn();
      setGhosts(data.ghosts || []);
    } catch (err) {
      console.error("Ghost scan failed:", err);
      setGhostError(err?.message || "Scan failed. Try again.");
    } finally {
      setGhostScanning(false);
    }
  };

  // Pull every user's email to the clipboard as a comma-separated list — drop it
  // straight into a Gmail BCC field for an announcement. Dedupes + skips blanks.
  const copyEmails = async () => {
    const emails = [...new Set(users.map((u) => (u.email || "").trim()).filter(Boolean))];
    const text = emails.join(", ");
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCount(emails.length);
      setTimeout(() => setCopiedCount(null), 3000);
    } catch {
      window.prompt("Copy these emails:", text);
    }
  };

  useEffect(() => {
    if (!allowed) return;
    const fetchAll = async () => {
      try {
        // Schools first, so we can map schoolId -> details without N extra reads.
        const schoolSnap = await getDocs(collection(firestore, "schools"));
        const schoolMap = {};
        schoolSnap.docs.forEach((d) => (schoolMap[d.id] = { id: d.id, ...d.data() }));

        const usersSnap = await getDocs(collection(firestore, "users"));

        const usersWithDetails = await Promise.all(
          usersSnap.docs.map(async (userDoc) => {
            const userData = userDoc.data();
            const userId = userDoc.id;
            const school = schoolMap[userData.schoolId];

            const studentsSnap = await getDocs(
              query(collection(firestore, "students"), where("addedBy", "==", userId))
            );
            const signoutSnap = await getDocs(
              collection(firestore, `users/${userId}/signedInHistory`)
            );
            const classesSnap = await getDocs(
              query(collection(firestore, "classes"), where("teacherId", "==", userId))
            );
            const activeClassCount = classesSnap.docs.filter((d) => !d.data().isArchived).length;
            const archivedClassCount = classesSnap.docs.filter((d) => d.data().isArchived).length;

            // Recency/frequency from sign-out history (measures USE, not just setup).
            const now = Date.now();
            const signoutTimes = signoutSnap.docs
              .map((d) => toDate(d.data().time_out))
              .filter(Boolean);
            const signouts7 = signoutTimes.filter((t) => now - t.getTime() <= 7 * DAY).length;
            const signouts30 = signoutTimes.filter((t) => now - t.getTime() <= 30 * DAY).length;
            const activeDays30 = new Set(
              signoutTimes
                .filter((t) => now - t.getTime() <= 30 * DAY)
                .map((t) => t.toDateString())
            ).size;
            const lastSignoutAt = signoutTimes.reduce(
              (max, t) => (!max || t > max ? t : max),
              null
            );
            // "Last seen" = the most recent of the app-open stamp (new) or a sign-out
            // (covers teachers active before lastActive existed / who only use other features).
            const lastActiveAt = toDate(userData.lastActive);
            const lastSeen = [lastActiveAt, lastSignoutAt]
              .filter(Boolean)
              .reduce((max, t) => (!max || t > max ? t : max), null);
            const signupAt = toDate(userData.acceptedTermsAt);

            return {
              uid: userId,
              ...userData,
              fullName: `${userData.title || ""} ${userData.firstName || ""} ${userData.lastName || ""}`.trim() || "—",
              schoolId: userData.schoolId || null,
              schoolName: school?.schoolName || "—",
              schoolAddress: school
                ? `${school.address || ""}, ${school.city || ""}, ${school.state || ""} ${school.zipcode || ""}`.replace(/^[,\s]+|[,\s]+$/g, "")
                : "—",
              activeClassCount,
              archivedClassCount,
              studentCount: studentsSnap.size,
              signoutCount: signoutSnap.size,
              signouts7,
              signouts30,
              activeDays30,
              lastSeen,
              signupAt,
            };
          })
        );

        // Roll users up into per-school rows. Skip provisional schools — every teacher
        // gets one auto-created at signup ("<Name>'s Class") as a school-of-one, and the
        // rest of the app hides them (see SchoolDropdown). They'd otherwise flood this
        // table with rows that are really just solo teachers, who already show up in the
        // Users table below.
        const schoolRows = schoolSnap.docs
          .filter((d) => d.data().provisional !== true)
          .map((d) => {
            const s = { id: d.id, ...d.data() };
            const members = usersWithDetails.filter((u) => u.schoolId === d.id);
            return {
              ...s,
              memberCount: members.length,
              adminCount: members.filter((u) => u.role === "admin").length,
              classCount: members.reduce((a, u) => a + u.activeClassCount + u.archivedClassCount, 0),
              studentCount: members.reduce((a, u) => a + u.studentCount, 0),
              signoutCount: members.reduce((a, u) => a + u.signoutCount, 0),
            };
          });

        // Count solo teachers (accounts still on their auto-created "school of one") so
        // that population stays visible even though it's filtered out of the table above.
        const provisionalIds = new Set(
          schoolSnap.docs.filter((d) => d.data().provisional === true).map((d) => d.id)
        );
        setSoloTeachers(usersWithDetails.filter((u) => provisionalIds.has(u.schoolId)).length);

        setUsers(usersWithDetails);
        setSchools(schoolRows.sort((a, b) => b.memberCount - a.memberCount));
      } catch (error) {
        console.error("Error fetching admin data:", error);
      } finally {
        setFetching(false);
      }
    };
    fetchAll();
  }, [allowed]);

  // In-app "Contact support" messages (Settings → Help). Newest first.
  useEffect(() => {
    if (!allowed) return;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(firestore, "supportMessages"), orderBy("createdAt", "desc"), limit(100))
        );
        setSupportMsgs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching support messages:", err);
      }
    })();
  }, [allowed]);

  const toggleSupportResolved = async (m) => {
    const next = m.status === "resolved" ? "new" : "resolved";
    try {
      await updateDoc(doc(firestore, "supportMessages", m.id), { status: next });
      setSupportMsgs((prev) => prev.map((x) => (x.id === m.id ? { ...x, status: next } : x)));
    } catch (err) {
      console.error("Error updating support message:", err);
    }
  };

  const deleteSupportMsg = async (m) => {
    if (!window.confirm("Delete this support message?")) return;
    try {
      await deleteDoc(doc(firestore, "supportMessages", m.id));
      setSupportMsgs((prev) => prev.filter((x) => x.id !== m.id));
    } catch (err) {
      console.error("Error deleting support message:", err);
    }
  };

  const totals = useMemo(() => {
    const now = Date.now();
    const seenWithin = (u, days) => u.lastSeen && now - u.lastSeen.getTime() <= days * DAY;
    const signedUpWithin = (u, days) => u.signupAt && now - u.signupAt.getTime() <= days * DAY;
    return {
      users: users.length,
      schools: schools.length,
      classes: users.reduce((a, u) => a + u.activeClassCount + u.archivedClassCount, 0),
      signouts: users.reduce((a, u) => a + u.signoutCount, 0),
      activeToday: users.filter((u) => seenWithin(u, 1)).length,
      active7: users.filter((u) => seenWithin(u, 7)).length,
      active30: users.filter((u) => seenWithin(u, 30)).length,
      newThisWeek: users.filter((u) => signedUpWithin(u, 7)).length,
      newThisMonth: users.filter((u) => signedUpWithin(u, 30)).length,
    };
  }, [users, schools]);

  const visibleUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const list = !q
      ? users
      : users.filter(
          (u) =>
            u.fullName.toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            u.schoolName.toLowerCase().includes(q)
        );
    // Most-recently-active first, so dormant accounts sink to the bottom.
    return [...list].sort((a, b) => (b.lastSeen?.getTime() || 0) - (a.lastSeen?.getTime() || 0));
  }, [users, userSearch]);

  // Removes the user's Firestore profile (role, schoolId, classPrefs, etc.). NOTE: the
  // client SDK cannot delete the Firebase Auth account itself — that needs the Admin SDK
  // / console — so the person can still sign in, but lands with no profile.
  const removeUser = async (u) => {
    if (u.uid === uid) {
      window.alert("You can't remove your own account here.");
      return;
    }
    if (!window.confirm(`Remove ${u.fullName} (${u.email})?\n\nThis deletes their ClassBoon profile and role. Their sign-in account is not affected.`)) {
      return;
    }
    try {
      await deleteDoc(doc(firestore, "users", u.uid));
      setUsers((prev) => prev.filter((x) => x.uid !== u.uid));
    } catch (err) {
      console.error("Error removing user:", err);
      window.alert("Could not remove this user. Check the console for details.");
    }
  };

  if (userLoading) {
    return <div className="admin-panel-container"><h1>Admin Panel</h1><p>Loading…</p></div>;
  }

  if (!allowed) {
    return (
      <div className="admin-panel-container">
        <h1>Admin Panel</h1>
        <p>This console is restricted to the ClassBoon super-admin account. Sign in with that account to continue.</p>
      </div>
    );
  }

  return (
    <div className="admin-panel-container">
      <h1>Admin Panel — Platform Overview</h1>

      <div className="admin-panel-summary-boxes">
        <div className="admin-panel-summary-box"><h3>Schools</h3><h3>{totals.schools}</h3></div>
        <div className="admin-panel-summary-box"><h3>Solo teachers</h3><h3>{soloTeachers}</h3></div>
        <div className="admin-panel-summary-box"><h3>Total Users</h3><h3>{totals.users}</h3></div>
        <div className="admin-panel-summary-box"><h3>Total Classes</h3><h3>{totals.classes}</h3></div>
        <div className="admin-panel-summary-box"><h3>Total Signouts</h3><h3>{totals.signouts}</h3></div>
      </div>

      <h2 style={{ marginTop: 28 }}>Engagement</h2>
      <p style={{ marginTop: 0, color: "var(--color-secondary)", fontSize: 14 }}>
        Who's actually <em>using</em> it — active = opened the app or signed a student out in the window.
      </p>
      <div className="admin-panel-summary-boxes">
        <div className="admin-panel-summary-box"><h3>Active today</h3><h3>{totals.activeToday}</h3></div>
        <div className="admin-panel-summary-box"><h3>Active this week</h3><h3>{totals.active7}</h3></div>
        <div className="admin-panel-summary-box"><h3>Active this month</h3><h3>{totals.active30}</h3></div>
        <div className="admin-panel-summary-box"><h3>New this week</h3><h3>{totals.newThisWeek}</h3></div>
        <div className="admin-panel-summary-box"><h3>New this month</h3><h3>{totals.newThisMonth}</h3></div>
      </div>

      {fetching && <p>Loading platform data…</p>}

      <h2>Schools</h2>
      <p style={{ marginTop: 0, color: "var(--color-secondary)", fontSize: 14 }}>
        Real, named schools only. Solo teachers on an auto-created “school of one” are counted
        in the tile above and listed individually in the Users table below.
      </p>
      <div className="admin-panel-table-container admin-hide-on-mobile">
        <table className="table">
          <thead>
            <tr>
              <th>School</th>
              <th>Location</th>
              <th>Members</th>
              <th>Admins</th>
              <th>Classes</th>
              <th>Students</th>
              <th>Signouts</th>
            </tr>
          </thead>
          <tbody>
            {schools.map((s) => (
              <tr key={s.id}>
                <td>{s.schoolName || "—"}</td>
                <td>{[s.city, s.state].filter(Boolean).join(", ") || "—"}</td>
                <td>{s.memberCount}</td>
                <td>{s.adminCount}</td>
                <td>{s.classCount}</td>
                <td>{s.studentCount}</td>
                <td>{s.signoutCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MobileTableCards
        items={schools}
        getKey={(s) => s.id}
        getTitle={(s) => s.schoolName || "—"}
        getSubtitle={(s) => {
          const loc = [s.city, s.state].filter(Boolean).join(", ");
          return `${loc ? loc + " · " : ""}${s.studentCount} students`;
        }}
        getFields={(s) => [
          ["Location", [s.city, s.state].filter(Boolean).join(", ") || "—"],
          ["Members", s.memberCount],
          ["Admins", s.adminCount],
          ["Classes", s.classCount],
          ["Students", s.studentCount],
          ["Signouts", s.signoutCount],
        ]}
      />

      <h2 style={{ marginTop: 32 }}>Users</h2>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Search by name, email, or school"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          style={{ width: "100%", maxWidth: 360, boxSizing: "border-box", margin: 0 }}
        />
        <button type="button" onClick={copyEmails} style={{ whiteSpace: "nowrap" }}>
          {copiedCount != null ? `✓ Copied ${copiedCount} emails` : `Copy all emails (${users.length})`}
        </button>
      </div>
      <div className="admin-panel-table-container admin-hide-on-mobile">
        <table className="table">
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Email</th>
              <th>School Name</th>
              <th>Last Active</th>
              <th>Signouts 7d</th>
              <th>Signouts 30d</th>
              <th>Active Days 30d</th>
              <th>Active Classes</th>
              <th>Students Added</th>
              <th>Signouts</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((u) => (
              <tr key={u.uid}>
                <td>{u.fullName}</td>
                <td>{u.email}</td>
                <td>{u.schoolName}</td>
                <td title={u.lastSeen ? u.lastSeen.toLocaleString() : "no activity recorded"}>
                  {timeAgo(u.lastSeen)}
                </td>
                <td>{u.signouts7}</td>
                <td>{u.signouts30}</td>
                <td>{u.activeDays30}</td>
                <td>{u.activeClassCount}</td>
                <td>{u.studentCount}</td>
                <td>{u.signoutCount}</td>
                <td>
                  <select
                    defaultValue={u.role || "teacher"}
                    onChange={async (e) => {
                      await updateDoc(doc(firestore, "users", u.uid), { role: e.target.value });
                    }}
                    style={{ width: "auto" }}
                  >
                    <option value="teacher">Teacher</option>
                    <option value="admin">Admin</option>
                    <option value="security">Security</option>
                  </select>
                </td>
                <td>
                  {u.uid === uid ? (
                    <span style={{ fontSize: 12, color: "var(--color-secondary)" }}>You</span>
                  ) : (
                    <button
                      onClick={() => removeUser(u)}
                      style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MobileTableCards
        items={visibleUsers}
        getKey={(u) => u.uid}
        getTitle={(u) => u.fullName || u.email}
        getSubtitle={(u) => `${u.schoolName || "—"} · ${u.role || "teacher"}`}
        getFields={(u) => [
          ["Email", u.email],
          ["School", u.schoolName],
          ["Last active", timeAgo(u.lastSeen)],
          ["Signouts 7d", u.signouts7],
          ["Signouts 30d", u.signouts30],
          ["Active days 30d", u.activeDays30],
          ["Active classes", u.activeClassCount],
          ["Students added", u.studentCount],
          ["Signouts", u.signoutCount],
        ]}
        renderExtra={(u) => (
          <>
            <select
              defaultValue={u.role || "teacher"}
              onChange={async (e) => {
                await updateDoc(doc(firestore, "users", u.uid), { role: e.target.value });
              }}
              style={{ width: "auto", margin: 0 }}
            >
              <option value="teacher">Teacher</option>
              <option value="admin">Admin</option>
              <option value="security">Security</option>
            </select>
            {u.uid === uid ? (
              <span style={{ fontSize: 12, color: "var(--color-secondary)" }}>You</span>
            ) : (
              <button
                onClick={() => removeUser(u)}
                style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", cursor: "pointer" }}
              >
                Remove
              </button>
            )}
          </>
        )}
      />

      <h2 style={{ marginTop: 32 }}>
        Support messages
        {supportMsgs.some((m) => m.status !== "resolved") && (
          <span style={{
            marginLeft: 10, background: "#dc2626", color: "#fff", borderRadius: 999,
            fontSize: 13, fontWeight: 700, padding: "2px 10px",
          }}>
            {supportMsgs.filter((m) => m.status !== "resolved").length} new
          </span>
        )}
      </h2>
      <p style={{ marginTop: 0, color: "var(--color-secondary)", fontSize: 14 }}>
        In-app messages from Settings → Help. Emailed messages still go to support@classboon.com.
      </p>
      {supportMsgs.length === 0 ? (
        <p style={{ color: "var(--color-secondary)" }}>No messages yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {supportMsgs.map((m) => {
            const resolved = m.status === "resolved";
            return (
              <div
                key={m.id}
                style={{
                  border: "1px solid var(--color-border)", borderRadius: 8, padding: "12px 14px",
                  background: resolved ? "#f6f7f9" : "#fff", opacity: resolved ? 0.7 : 1,
                }}
              >
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <strong>{m.name || "(no name)"}</strong>
                  {m.category && (
                    <span style={{ background: "#eef1f4", borderRadius: 999, fontSize: 12, padding: "1px 9px" }}>
                      {m.category}
                    </span>
                  )}
                  <span style={{ color: "var(--color-secondary)", fontSize: 13 }}>
                    {m.schoolName || "—"} · {timeAgo(toDate(m.createdAt))}
                  </span>
                </div>
                <div style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{m.message}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", fontSize: 13 }}>
                  {m.email && (
                    <a href={`mailto:${m.email}?subject=Re:%20your%20classBoon%20message`}>Reply by email</a>
                  )}
                  <span style={{ color: "var(--color-secondary)" }}>{m.page || ""}</span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <button
                      onClick={() => toggleSupportResolved(m)}
                      style={{ padding: "4px 10px", borderRadius: 6, cursor: "pointer" }}
                    >
                      {resolved ? "Reopen" : "Mark resolved"}
                    </button>
                    <button
                      onClick={() => deleteSupportMsg(m)}
                      style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}
                    >
                      Delete
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h2 style={{ marginTop: 32 }}>
        Ghost accounts
        {ghosts && ghosts.length > 0 && (
          <span style={{
            marginLeft: 10, background: "#dc2626", color: "#fff", borderRadius: 999,
            fontSize: 13, fontWeight: 700, padding: "2px 10px",
          }}>
            {ghosts.length}
          </span>
        )}
      </h2>
      <p style={{ marginTop: 0, color: "var(--color-secondary)", fontSize: 14 }}>
        Signed-in accounts with no profile (school-less — usually a Google login before the
        profile fix). They self-heal the next time they log in; this just surfaces stragglers.
      </p>
      <button onClick={scanGhosts} disabled={ghostScanning} style={{ padding: "6px 14px", borderRadius: 6, cursor: "pointer" }}>
        {ghostScanning ? "Scanning…" : ghosts ? "Re-scan" : "Scan for ghost accounts"}
      </button>
      {ghostError && <p style={{ color: "#dc2626", fontSize: 13 }}>{ghostError}</p>}
      {ghosts && ghosts.length === 0 && !ghostScanning && (
        <p style={{ color: "var(--color-secondary)", marginTop: 12 }}>✓ No ghost accounts — everyone has a profile.</p>
      )}
      {ghosts && ghosts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
          {ghosts.map((g) => (
            <div
              key={g.uid}
              style={{ border: "1px solid var(--color-border)", borderRadius: 8, padding: "10px 12px", fontSize: 14 }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <strong>{g.email || "(no email)"}</strong>
                {g.providers.map((p) => (
                  <span key={p} style={{ background: "#eef1f4", borderRadius: 999, fontSize: 12, padding: "1px 9px" }}>
                    {p.replace(".com", "")}
                  </span>
                ))}
                <span style={{ marginLeft: "auto", color: "var(--color-secondary)", fontSize: 13 }}>
                  last seen {timeAgo(toDate(g.lastSignInTime))}
                </span>
              </div>
              <div style={{ color: "var(--color-secondary)", fontSize: 12, marginTop: 4 }}>
                {g.displayName ? `${g.displayName} · ` : ""}created {timeAgo(toDate(g.creationTime))} · uid {g.uid}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
